import styled, { css } from 'styled-components';
import {
  Chip,
  Flex,
  FlexColumn,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { Tooltip } from '@mui/material';

const Wrapper = styled(FlexColumn)<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  width: 480px;
  height: 176px;
  padding: 24px;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.secondary.main : theme.palette.common.gray5};
  background: ${({ theme, $selected }) =>
    $selected ? theme.palette.common.lightBlue6 : theme.palette.common.white};
  border-radius: 8px;
  gap: 16px;

  cursor: pointer;
  user-select: none;

  :hover {
    background: ${({ theme, $selected }) =>
      $selected ? theme.palette.common.lightBlue6 : theme.palette.common.gray3};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      filter: grayscale(100%) brightness(85%);
      background-color: ${({ theme }) => theme.palette.common.gray8};
      cursor: not-allowed;

      :hover {
        background: ${({ theme }) => theme.palette.common.gray5};
      }

      /* Disable hover effects for children */
      & > * {
        pointer-events: none;
      }
    `}
`;

const StyledChip = styled(Chip)<{ $isCloud?: boolean }>`
  background-color: ${({ theme, $isCloud }) =>
    $isCloud ? theme.palette.common.lightBlue4 : 'transparent'};
`;

type Props = {
  testName: string;
  packageName: string;
  description: string;
  onClick: () => void;
  selected?: boolean;
  disabledReason?: string;
  comingSoon?: boolean;
};

const TestCard = ({
  testName,
  packageName,
  description,
  onClick,
  selected,
  disabledReason,
  comingSoon,
}: Props) => {
  const isElementaryCloud = isElementaryCloudTest(packageName?.toLowerCase());

  return (
    <Tooltip title={disabledReason} placement="top" followCursor>
      <Wrapper
        onClick={onClick}
        $selected={selected}
        $disabled={!!disabledReason}
      >
        <Flex sx={{ justifyContent: 'space-between' }}>
          <StyledChip
            sx={{ width: 'min-content' }}
            label={packageName?.toLowerCase()}
            size="medium"
            variant="outlined"
            $isCloud={isElementaryCloud}
            bold
          />
          {comingSoon && (
            <Chip label="Coming soon" size="small" variant="outlined" />
          )}
        </Flex>
        <FlexColumn sx={{ gap: 1 }}>
          <OverflowTooltip bold autoTooltipPlacement>
            {testName}
          </OverflowTooltip>
          <OverflowTooltip
            sx={{ color: 'common.gray2' }}
            maxLines={3}
            autoTooltipPlacement={!!disabledReason}
          >
            {description}
          </OverflowTooltip>
        </FlexColumn>
      </Wrapper>
    </Tooltip>
  );
};

export default TestCard;
